import { useMemo, useState } from "react";
import { isPositiveMinor } from "../../utils/money";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ForexDeliveryDialog, {
  DeliveryStatusDialog,
  NEXT_STATUSES,
  type DeliveryLine,
} from "pages/GenericOrderDetailsPage/components/ForexDeliveryDialog";
import { useForexAccess } from "pages/GenericOrderDetailsPage/hooks/useForexAccess";
import { setDeclaredOnArrival } from "service/moneeflo/legacy-forex-desk-api";
import type { CoreDelivery } from "service/moneeflo/legacy-forex-order-api";

const STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  ready_to_deliver: "Ready to deliver",
  delivered: "Delivered",
  failed: "Failed",
  cancelled: "Cancelled",
};
const MODE_LABEL: Record<string, string> = { self: "Delivery", courier: "Courier", pickup: "Pickup" };

type OrderLine = {
  id: string;
  name: string;
  quantity: number;
  status: string;
  country_currency_want: string;
};

const addressText = (d: CoreDelivery) =>
  d.address
    ? [d.address.line1, d.address.line2, d.address.landmark, d.address.city, d.address.state, d.address.postalCode]
        .filter(Boolean)
        .join(", ")
    : "Picked up at the branch";

/**
 * The order's deliveries, as core keeps them. Each one carries lines and how
 * much of each; it can be edited, and moved on through its statuses. Whether it
 * may go out is core's call (a sale has to be paid in full, an encashment needs
 * its customs declaration seen), and when core refuses, the dialog shows why.
 */
export const Delivery = ({
  deliveries,
  lines,
  isEncashment,
  declaredOnArrival,
  customerId,
  defaultAddressId,
  disabled,
}: {
  deliveries: CoreDelivery[];
  lines: OrderLine[];
  isEncashment: boolean;
  declaredOnArrival: boolean;
  customerId: string | null;
  defaultAddressId?: string | null;
  /** The order cannot take deliveries now: not yet accepted, cancelled or closed. */
  disabled: boolean;
}) => {
  const { canChangeDelivery, canChangeDeliveryStatus, canEditOrder } = useForexAccess();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<CoreDelivery | null>(null);
  const [moving, setMoving] = useState<CoreDelivery | null>(null);

  const declaration = useMutation(() => setDeclaredOnArrival(true), {
    onSuccess: () => {
      toast.success("Customs declaration recorded");
      queryClient.invalidateQueries(["orderDetails"]);
    },
    onError: (e: unknown) => {
      toast.error("The declaration could not be recorded", { description: e instanceof Error ? e.message : undefined });
    },
  });

  // Foreign currency still to be put on a delivery that is going ahead.
  const offered: DeliveryLine[] = useMemo(() => {
    // Worked in paise (BigInt) so a line is never left with a rounding sliver or over-delivered.
    const onLive = new Map<string, bigint>();
    for (const d of deliveries) {
      if (d.status === "failed" || d.status === "cancelled") continue;
      for (const item of d.items) {
        onLive.set(item.lineId, (onLive.get(item.lineId) ?? 0n) + BigInt(item.fxAmountMinor));
      }
    }
    return lines
      .filter((l) => l.status !== "Reject")
      .map((l) => ({
        id: l.id,
        // The foreign currency is the one that is handed over: for an encashment it is the one the customer has.
        label: `${l.name} ${isEncashment ? (l as { country_currency_have?: string }).country_currency_have ?? "" : l.country_currency_want} ${l.quantity}`,
        currency: isEncashment ? (l as { country_currency_have?: string }).country_currency_have ?? "" : l.country_currency_want,
        quantity: l.quantity,
        remainingMinor: (() => {
          const left = BigInt((l as { fx_amount_minor?: string }).fx_amount_minor ?? "0") - (onLive.get(l.id) ?? 0n);
          return left > 0n ? left.toString() : "0";
        })(),
      }));
  }, [deliveries, lines, isEncashment]);

  const canAdd = canChangeDelivery && !disabled && offered.some((l) => isPositiveMinor(l.remainingMinor));

  return (
    <div className="container mx-auto flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {isEncashment ? (
          <p className="flex items-center gap-2 text-sm text-[#333333]">
            Customs declaration on arrival:{" "}
            <span className={declaredOnArrival ? "font-medium text-[#00701D]" : "font-medium text-[#7A5200]"}>
              {declaredOnArrival ? "Seen" : "Not seen yet"}
            </span>
            {!declaredOnArrival && canEditOrder && !disabled && (
              <Button
                type="button"
                variant="ghost"
                disabled={declaration.isLoading}
                onClick={() => declaration.mutate()}
                className="h-auto cursor-pointer rounded-lg border border-blue-700 px-3 py-1.5 text-sm text-blue-700"
              >
                Mark as seen
              </Button>
            )}
          </p>
        ) : (
          <span />
        )}
        {canAdd && (
          <Button
            onClick={() => setAdding(true)}
            className="cursor-pointer rounded-lg border border-blue-700 text-blue-700 hover:bg-[#E8F0FE]"
            variant="ghost"
          >
            Add delivery
          </Button>
        )}
      </div>

      {deliveries.length === 0 ? (
        <p className="py-6 text-center text-sm text-[#666666]">No deliveries yet.</p>
      ) : (
        <>
        {/* On a phone (this project's md: is the narrow side) each delivery is a card, so its status and actions are never off the edge. */}
        <ul className="hidden flex-col gap-3 md:flex">
          {deliveries.map((d) => {
            const next = NEXT_STATUSES[d.status] ?? [];
            return (
              <li key={d.id} data-testid="delivery-card" className="rounded-lg border border-[#E5E5E5] p-3 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    {d.items.map((item) => (
                      <div key={item.lineId} className="font-medium text-[#333333]">
                        {item.product} {item.currency} {Number(item.fxAmountMinor) / 100}
                      </div>
                    ))}
                    <div className="text-[#666666]">
                      {MODE_LABEL[d.mode] ?? d.mode}
                      {d.channel ? ` · ${d.channel}` : ""}
                      {d.scheduledOn
                        ? ` · ${new Date(d.scheduledOn).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`
                        : ""}
                    </div>
                  </div>
                  <span className="shrink-0 rounded-md bg-[#F4F4F4] px-2 py-0.5 text-xs font-medium">{STATUS_LABEL[d.status] ?? d.status}</span>
                </div>
                <p className="mt-2 text-[#333333]">{addressText(d)}</p>
                {d.assignedTo ? <p className="text-[#666666]">Assigned to {d.assignedTo.name}</p> : null}
                {d.notes ? <p className="text-[#666666]">{d.notes}</p> : null}
                {!disabled && next.length > 0 && (
                  <div className="mt-2 flex gap-4">
                    {canChangeDeliveryStatus && (
                      <button type="button" onClick={() => setMoving(d)} className="cursor-pointer font-medium text-[#165FE3] underline underline-offset-2">
                        Update status
                      </button>
                    )}
                    {canChangeDelivery && (
                      <button type="button" onClick={() => setEditing(d)} className="cursor-pointer font-medium text-[#165FE3] underline underline-offset-2">
                        Edit
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <div className="overflow-x-auto md:hidden">
          <Table className="min-w-[640px] rounded-t-lg">
            <TableHeader className="bg-[#EEEEEE]">
              <TableRow className="whitespace-nowrap text-sm font-medium text-[#666666]">
                <TableHead>Currency</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Assigned to</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveries.map((d) => {
                const next = NEXT_STATUSES[d.status] ?? [];
                return (
                  <TableRow key={d.id} data-testid="delivery-row" className="align-top">
                    <TableCell>
                      {d.items.map((item) => (
                        <div key={item.lineId}>
                          {item.product} {item.currency} {Number(item.fxAmountMinor) / 100}
                        </div>
                      ))}
                    </TableCell>
                    <TableCell>
                      <span>{MODE_LABEL[d.mode] ?? d.mode}</span>
                      {d.channel ? <div className="text-sm text-[#666666]">{d.channel}</div> : null}
                      {d.scheduledOn ? (
                        <div className="text-sm text-[#666666]">
                          {new Date(d.scheduledOn).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="max-w-[220px]">{addressText(d)}</TableCell>
                    <TableCell>{d.assignedTo?.name ?? "-"}</TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1.5">
                        <span data-testid="delivery-status">{STATUS_LABEL[d.status] ?? d.status}</span>
                        {d.notes ? <span className="text-sm text-[#666666]">{d.notes}</span> : null}
                        <div className="flex gap-3 text-sm">
                          {canChangeDeliveryStatus && !disabled && next.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setMoving(d)}
                              className="cursor-pointer font-medium text-[#165FE3] underline underline-offset-2 hover:text-[#1942B8]"
                            >
                              Update status
                            </button>
                          )}
                          {canChangeDelivery && !disabled && next.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setEditing(d)}
                              className="cursor-pointer font-medium text-[#165FE3] underline underline-offset-2 hover:text-[#1942B8]"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        </>
      )}

      <ForexDeliveryDialog
        open={adding}
        onOpenChange={setAdding}
        customerId={customerId}
        lines={offered}
        defaultAddressId={defaultAddressId}
      />
      <ForexDeliveryDialog
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
        customerId={customerId}
        lines={offered}
        delivery={editing}
      />
      <DeliveryStatusDialog open={!!moving} onOpenChange={(open) => !open && setMoving(null)} delivery={moving} />
    </div>
  );
};
